"use client";

import { Calendar, LogOut, Mail, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { OrganizationsOutput } from "@/interfaces";
import { useSidebar } from "@/components/ui/sidebar";
import { formatDddPhone } from "@/util/formatters";

interface OrganizationHeaderProps {
  organization: OrganizationsOutput;
}

export function OrganizationInfo({ organization }: OrganizationHeaderProps) {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();

  const handleSwitchOrganization = () => {
    navigate("/organizacoes");
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <TooltipProvider>
      <div className="bg-gradient-to-r from-[#EE7B11] to-[#FF9F45] p-4 rounded-lg shadow-md mb-2 mx-2">
        <div className="flex items-center justify-between mb-3">
          <img
            src="https://fly.storage.tigris.dev/pedidoz-public-images/landing-page/logo-completa-sem-fundo2.PNG"
            alt="Pedidoz Logo"
            className="h-8 w-auto bg-white p-1 rounded"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSwitchOrganization}
                className="hover:bg-black/20 text-white shrink-0 h-8 w-8"
                aria-label="Trocar organização"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            {!isMobile && (
              <TooltipContent side="right">
                <p>Trocar Organização</p>
              </TooltipContent>
            )}
          </Tooltip>
        </div>
        <div className="text-white">
          <h2 className="font-bold text-base mb-2 break-words">
            {organization.name}
          </h2>
          <div className="space-y-1 text-xs">
            {organization.phoneNumber && (
              <p className="flex items-center">
                <Phone className="h-3.5 w-3.5 mr-2 shrink-0" />
                <span className="break-all">
                  {formatDddPhone(
                    organization.ddd,
                    organization.phoneNumber,
                    organization.internationalCode
                  )}
                </span>
              </p>
            )}
            {organization.email && (
              <p className="flex items-center">
                <Mail className="h-3.5 w-3.5 mr-2 shrink-0" />
                <span className="break-all overflow-hidden text-ellipsis">
                  {organization.email}
                </span>
              </p>
            )}
            <p className="flex items-center">
              <Calendar className="h-3.5 w-3.5 mr-2 shrink-0" />
              <span className="break-normal">
                Plano ativo até: {formatDate(organization?.plan?.endDate)}
              </span>
            </p>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
